import { useState, useEffect } from 'preact/hooks';
import useVendor from './useVendor';
import { publicConfigurationUrl, publicConfigurationManagedUrl } from '../config/serviceUrl';
import { HookState } from '../types/hooks';

/**
 * Fetch storefront public configuration
 * @return {{data: Object|null, loading: boolean, error: boolean}}
 */
export default function useStorefrontConfig(): HookState {
    const [status, setStatus] = useState({
        loading: true,
        error: false,
        data: null,
    });

    const vendor = useVendor();

    useEffect(() => {
        (async () => {
            // Declaring these variables inside try{...} statement
            // leads to a babel compilation error when running Jest tests.
            let response, configuration, managedConfiguration;

            try {
                // Merges the base public config with the CMS-managed config (e.g.
                // menu items toggled via CMS, like the wedding link) — managed
                // values win, same as MyAccount/Authentication-Frontend. Not every
                // vendor has a managed config yet (the source 403s for those), so
                // that half is best-effort and never fails the whole page.
                [response, managedConfiguration] = await Promise.all([
                    fetch(publicConfigurationUrl + vendor),
                    fetch(publicConfigurationManagedUrl + vendor + '.json')
                        .then((managedResponse) => (managedResponse.ok ? managedResponse.json() : {}))
                        .catch(() => ({})),
                ]);

                if (!response.ok) {
                    throw new Error(`${response.status} ${response.statusText}`);
                }

                configuration = await response.json();

                setStatus({ loading: false, error: false, data: { ...configuration, ...managedConfiguration } });
            } catch (error: any) {
                console.error(`Public configuration error: ${error.message}`);
                setStatus({ loading: false, error: true, data: null });
            }
        })();
    }, [vendor]);

    return status;
}
